
import {
  Mixins,
  Component,
  Provide,
  Prop,
  Inject
} from "vue-property-decorator";
import { CreateElement } from "vue";
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data";
import { ITicketMessage } from "@/models/tickets";
// import { ISocketMessage } from "@/models/socket";
import {
  ITicketProvider,
  ITicketFeedProviderMethods,
  ITicketFeedProvider
} from "@/models/providers/ticket";
import { HookCodes } from "@/data/enums/hooks";
import { IUploadFile } from "@/models/uploadFiles";
import Context from "@/mixins/context";
import { TicketStatusCodes } from "@/data/enums/tickets";
import { IHookLog } from "@/models/hooks";
import _ from "@/boot/lodash";

@Component
export default class TicketFeedProvider extends Mixins(
  RequestPromises,
  Context
) {
  // Inject

  @Inject({ from: "ticketProvider" })
  public readonly $ticketData!: () => ITicketProvider;

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data

  public hasMoreMessages = false;
  public hasRequestError = false;
  public isLoading = false;
  public isReloading = false;
  public logs: IHookLog[] = [];
  public messageFilters: Record<string, any> = {};
  public messages: ITicketMessage[] = [];
  public withLogs = true;

  // Lifecycle Methods

  public created() {
    this.setupSocketListeners();
  }

  public beforeDestroy() {
    this.destroySocketListeners();
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isLoading && this.hasRequestError },
        on: { click: () => this.fetchBackwards() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $ticketFeedData: this.providerData(),
        $ticketFeedMethods: this.providerMethods
      })
    );
  }

  // Computed

  public get ticketData() {
    return this.$ticketData();
  }

  public get ticket() {
    return this.ticketData.ticket;
  }

  public get orderedMessages() {
    return _.orderBy(this.messages, ["created_at"], ["desc"]);
  }

  public get orderedLogs() {
    return _.orderBy(this.logs, ["created_at"], ["desc"]);
  }

  public get filterMessageLogs() {
    return this.isClient
      ? { "filter[is_log]": 0 }
      : { "filter[action]": "null,delete" };
  }

  public get isPollable() {
    return ![TicketStatusCodes.CLOSED].includes(
      this.ticketData.ticketStatus?.code as TicketStatusCodes
    );
  }

  // Methods

  public resetFeed() {
    this.hasMoreMessages = false;
    this.messageFilters = {};
    this.withLogs = true;
    this.messages = [];
    this.logs = [];
  }

  public async fetchForwards() {
    try {
      this.isLoading = true;
      const newestMessage = _.first(this.orderedMessages);
      const newestLog = _.first(this.orderedLogs);
      await Promise.all([
        this.getMessages(
          { ...this.messageFilters, ["filter[id|gt]"]: newestMessage?.id },
          0
        ),
        this.withLogs
          ? this.getLogs({ ["filter[id|gt]"]: newestLog?.id, limit: 0 })
          : (Promise.resolve() as unknown)
      ]);
    } catch (error) {
      // Silent catch (non-critical)
    } finally {
      this.isLoading = false;
    }
  }

  public async fetchBackwards() {
    try {
      this.isLoading = true;
      this.hasRequestError = false;
      const oldestMessage = _.last(this.orderedMessages);
      const oldestLog = _.last(this.orderedLogs);
      const [hasMoreMessages] = await Promise.all([
        this.getMessages(
          {
            ...this.messageFilters,
            ["filter[id|lt]"]: oldestMessage?.id
          },
          this.messages?.length ? 20 : undefined
        ),
        this.withLogs
          ? this.getLogs({ ["filter[id|lt]"]: oldestLog?.id, limit: 0 })
          : (Promise.resolve() as unknown)
      ]);
      this.hasMoreMessages = hasMoreMessages ?? false;
    } catch (error) {
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
    }
  }

  public loadAll() {
    this.withLogs = true;
    this.messageFilters = {};
    return this.fetchBackwards();
  }

  public loadAttachments() {
    this.withLogs = false;
    this.messageFilters = { ["filter[files.id|gt]"]: 0 };
    return this.fetchBackwards();
  }

  public loadNotes() {
    this.withLogs = false;
    this.messageFilters = { ["filter[is_private]"]: 1 };
    return this.fetchBackwards();
  }

  public async getMessages(params = {}, limit = 10): Promise<boolean> {
    try {
      const reqLimit = limit ? limit + 1 : 0;
      const { data }: { data: ITicketMessage[] } = await this.$store.dispatch(
        `data/${DataModules.TICKETS_MESSAGES}/list`,
        {
          ignoreStored: true,
          storeData: false,
          ticketId: this.ticket.id,
          params: {
            order: "-id",
            with: "files",
            ...(this.ticketData.isStaged ? { with_staged_imports: 1 } : {}),
            ...this.filterMessageLogs,
            ...params,
            limit: reqLimit
          },
          returnData: false,
          vm: this
        }
      );

      this.messages.push(...(!limit ? data : [...data].splice(0, limit)));
      return data?.length > limit; // Here we return boolean to indicate whether there is more to load
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      return false;
    }
  }

  public async getLogs(params = {}) {
    let retrieved: any[] = [];
    let remaining = 0;
    try {
      const response = await this.$store.dispatch(
        `data/${DataModules.HOOKS_LOGS}/list`,
        {
          clientId: this.ticket.client_id,
          ignoreStored: true,
          storeData: false,
          params: {
            "filter[hook.code]": [
              HookCodes.TICKET_CLOSED,
              HookCodes.TICKET_OPENED,
              HookCodes.TICKET_REOPENED,
              HookCodes.TICKET_IN_PROGRESS,
              HookCodes.TICKET_CLIENT_REPLIED,
              HookCodes.TICKET_WAITING_RESPONSE
            ].join(","),
            "filter[object_type]": "ticket",
            "filter[object_id]": this.ticket.id,
            order: "-id",
            brandId: this.ticket.brand_id,
            with: "hook,object.status",
            ...(this.ticketData.isStaged ? { with_staged_imports: 1 } : {}),
            ...params
          },
          returnData: false,
          vm: this
        }
      );
      retrieved = this.$_.difference(response.data, this.logs);
      this.logs.push(...retrieved);
      remaining = Math.max(0, response.total - retrieved.length);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
    return { retrieved, remaining };
  }

  public getTicketMessage(messageId) {
    return this.$store.dispatch(`data/${DataModules.TICKETS_MESSAGES}/get`, {
      ticketId: this.ticket.id,
      messageId,
      storeData: false,
      vm: this
    });
  }

  public addMessage(message: ITicketMessage) {
    if (this.hasMessage(message.id)) return this.updateMessage(message);
    this.messages.unshift(message);
  }

  public updateMessage(message: ITicketMessage) {
    const index = this.$_.findIndex(this.messages, ["id", message.id]);
    if (index === -1) return;
    this.$set(this.messages, index, message);
  }

  public removeMessage(messageId: ITicketMessage["id"]) {
    this.messages = this.$_.filter(
      this.messages,
      message => message.id !== messageId
    );
  }

  public hasMessage(messageId: ITicketMessage["id"]) {
    return this.$_.findIndex(this.messages, ["id", messageId]) !== -1;
  }

  public setupSocketListeners() {
    this.destroySocketListeners();
    // Listen for new message
    // this.$bus.$on("socket-ticket-new-message", async (data: ISocketMessage) => {
    //   const { message_id, ticket_id } = JSON.parse(data.message);
    //   if (ticket_id !== this.ticket.id) return; // Different ticket
    //   if (this.hasMessage(message_id)) return; // Message already retrieved
    //   this.addMessage(await this.getTicketMessage(message_id));
    // });
    // Listen for updated message
    // this.$bus.$on(
    //   "socket-ticket-update-message",
    //   async (data: ISocketMessage) => {
    //     const { message_id, ticket_id } = JSON.parse(data.message);
    //     if (ticket_id !== this.ticket.id) return; // Different ticket
    //     this.addMessage(await this.getTicketMessage(message_id));
    //   }
    // );
    // Listen for delete message
    // this.$bus.$on(
    //   "socket-ticket-delete-message",
    //   async (data: ISocketMessage) => {
    //     const message = JSON.parse(data.message);
    //     if (message.ticket_id !== this.ticket.id) return; // Different ticket
    //     if (this.isAdmin) {
    //       this.addMessage(await this.getTicketMessage(message.log_message_id));
    //     }
    //     this.removeMessage(message.message_id);
    //   }
    // );
    // Listen for new status
    // this.$bus.$on("socket-ticket-status-update", () => {
    //   const newestLogId = this.$_.get(this.$_.first(this.logs), "id");
    //   this.getLogs({
    //     ["filter[id|gt]"]: newestLogId
    //   });
    // });
    // Listen for refresh
    this.$bus.$on("socket-ticket-refresh-feed", () => this.fetchForwards());
  }

  public destroySocketListeners() {
    // this.$bus.$off("socket-ticket-new-message");
    // this.$bus.$off("socket-ticket-update-message");
    // this.$bus.$off("socket-ticket-delete-message");
    // this.$bus.$off("socket-ticket-status-update");
    this.$bus.$off("socket-ticket-refresh-feed");
  }

  public removeFile(
    messageId: ITicketMessage["id"],
    fileId: IUploadFile["id"]
  ) {
    return this.$store.dispatch(
      `data/${DataModules.TICKETS_MESSAGES}/deleteFile`,
      {
        ticketId: this.ticket.id,
        messageId,
        fileId
      }
    );
  }

  public async downloadFile(fileId: IUploadFile["id"]) {
    return this.$store.dispatch(`data/${DataModules.TICKETS_FILES}/download`, {
      vm: this,
      fileId
    });
  }

  @Provide("ticketFeedProviderMethods")
  public get providerMethods(): ITicketFeedProviderMethods {
    return {
      addMessage: this.addMessage,
      downloadFile: this.downloadFile,
      getTicketMessage: this.getTicketMessage,
      loadAll: this.loadAll,
      loadAttachments: this.loadAttachments,
      loadNotes: this.loadNotes,
      removeFile: this.removeFile,
      removeMessage: this.removeMessage,
      resetFeed: this.resetFeed,
      updateMessage: this.updateMessage
    };
  }

  @Provide("ticketFeedProviderData")
  public providerData(): ITicketFeedProvider {
    return {
      hasMoreMessages: this.hasMoreMessages,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      logs: this.logs,
      messages: this.messages
    };
  }
}
